import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, User } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { scrollToId } from "../lib/api";

const LINKS = [
  { label: "Shop", id: "shop" },
  { label: "Categories", id: "categories" },
  { label: "Safety", id: "safety" },
  { label: "Delivery", id: "delivery" },
];

export default function Navbar() {
  const { count, setCartOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-obsidian/85 backdrop-blur-xl border-b border-ember/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className="font-mono text-[11px] tracking-[0.28em] uppercase text-muted-night hover:text-ember transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <a
              href="/account"
              data-testid="nav-account-link"
              className="hidden sm:flex items-center gap-2 rounded-full border border-starlight/15 bg-card/60 px-4 py-2 hover:border-ember/60 transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-4 w-4 rounded-full" />
              ) : (
                <User className="h-3.5 w-3.5 text-ember" />
              )}
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-starlight">
                {user.name?.split(" ")[0] || "Account"}
              </span>
            </a>
          ) : (
            <a
              href="/login"
              data-testid="nav-login-link"
              className="hidden sm:flex items-center gap-2 rounded-full border border-starlight/15 px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-night hover:border-ember/60 hover:text-ember transition-colors"
            >
              <User className="h-3.5 w-3.5" /> Sign in
            </a>
          )}
          <button
            data-testid="cart-drawer-toggle-button"
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 rounded-full border border-ember/30 bg-card/60 px-4 py-2 hover:border-ember/80 hover:bg-card-hover transition-all duration-300 group"
            aria-label="Open cart"
          >
            <ShoppingBag className="h-4 w-4 text-ember" />
            <span className="hidden sm:block font-mono text-[11px] tracking-[0.2em] uppercase text-starlight">
              Cart
            </span>
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ scale: 0.4, y: -6 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 18 }}
                className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-ember px-1 font-mono text-[10px] font-bold text-obsidian"
              >
                {count}
              </motion.span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
